import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

/*
 * Which helper:
 * - formatCivilDate / formatDisplayDate — EDI & claims “business” dates (DOS, DOB, YYYYMMDD). UTC calendar
 *   day only; no local TZ shift; time in the string is not shown.
 * - formatEmrDateInput — EMR/FHIR: civil for date-only and UTC-midnight; formatInstant when a real time exists.
 * - formatInstant — server instants (created_at, uploads, logs).
 */

/** X12 / FHIR compact calendar date → ISO date-only, or null if invalid. */
function tryCompactYmdToIso(s: string): string | null {
    const t = s.trim();
    if (!/^\d{8}$/.test(t)) return null;
    const monthNum = parseInt(t.slice(4, 6), 10);
    const dayNum = parseInt(t.slice(6, 8), 10);
    if (monthNum < 1 || monthNum > 12 || dayNum < 1 || dayNum > 31) return null;
    return `${t.slice(0, 4)}-${t.slice(4, 6)}-${t.slice(6, 8)}`;
}

export const formatCurrency = (amount: number, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
    }).format(amount);
};

/**
 * Calendar dates (DOB, DOD, DOS, EDI/EMR business dates): parse in UTC and format as
 * a single calendar day — no local-timezone day shift. Handles compact YYYYMMDD, ISO date-only,
 * and ISO datetimes (time is not shown; the UTC calendar day is used).
 */
export const formatCivilDate = (dateStr: string | null | undefined): string => {
    const d = parseCivilDateUtc(dateStr);
    return d ? d.format('MMM D, YYYY') : '-';
};

/**
 * Compact civil-date range (see {@link formatCivilDate} for parsing rules), e.g.
 * "Sep 21 – 23, 2025" (same month), "Sep 28 – Oct 3, 2025" (same year), or
 * "Dec 30, 2025 – Jan 2, 2026" (spanning years). Falls back to a single date
 * when `to` is missing or equal to `from`.
 */
export const formatDateRange = (
    from: string | null | undefined,
    to: string | null | undefined,
): string => {
    const start = parseCivilDateUtc(from);
    const end = parseCivilDateUtc(to);
    if (!start) return formatCivilDate(to);
    if (!end || start.isSame(end, 'day')) return formatCivilDate(from);
    if (start.isSame(end, 'month')) {
        return `${start.format('MMM D')} – ${end.format('D, YYYY')}`;
    }
    if (start.isSame(end, 'year')) {
        return `${start.format('MMM D')} – ${end.format('MMM D, YYYY')}`;
    }
    return `${start.format('MMM D, YYYY')} – ${end.format('MMM D, YYYY')}`;
};

export type FormatInstantOptions = {
    /** If true, only the local calendar date (e.g. table "Imported" column). Default false = date + time. */
    dateOnly?: boolean;
};

/** Instants from the server (ISO with time / Z): show in the user's local timezone. */
export const formatInstant = (
    isoString: string | null | undefined,
    options: FormatInstantOptions = {},
): string => {
    if (isoString == null || String(isoString).trim() === '') {
        return '-';
    }
    const date = new Date(isoString);
    if (isNaN(date.getTime()) || date.getTime() === 0) {
        return '-';
    }
    if (options.dateOnly) {
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        });
    }
    return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
};

/**
 * EDI / X12 business dates: civil calendar only (see {@link formatCivilDate}).
 */
export const formatDisplayDate = (input: string | null | undefined): string => {
    return formatCivilDate(input);
};

/** Parse EDI civil dates (same rules as {@link formatCivilDate}) to UTC start-of-day, or null if invalid/unset. */
function parseCivilDateUtc(input: string | null | undefined): dayjs.Dayjs | null {
    if (input == null || String(input).trim() === '') {
        return null;
    }
    let toParse = String(input).trim();
    const fromCompact = tryCompactYmdToIso(toParse);
    if (fromCompact !== null) {
        toParse = fromCompact;
    } else if (/^\d{8}$/.test(toParse)) {
        return null;
    }
    try {
        const d = dayjs.utc(toParse);
        if (!d.isValid()) {
            return null;
        }
        const epochMs = new Date(toParse).getTime();
        if (!isNaN(epochMs) && epochMs === 0) {
            return null;
        }
        if (d.format('YYYY-MM-DD') === '1970-01-01') {
            return null;
        }
        return d.startOf('day');
    } catch {
        return null;
    }
}

/**
 * Whole years from date of birth to a reference date (UTC calendar days).
 * Reference defaults to today (UTC) when omitted. Typical reference for claims is DOS (service from/to).
 */
export const formatAgeYearsFromBirthDate = (
    birthDate: string | null | undefined,
    asOfDate?: string | null,
): string => {
    const birth = parseCivilDateUtc(birthDate);
    if (!birth) {
        return '-';
    }
    const asOf =
        asOfDate != null && String(asOfDate).trim() !== ''
            ? parseCivilDateUtc(asOfDate)
            : dayjs.utc().startOf('day');
    if (!asOf || asOf.isBefore(birth)) {
        return '-';
    }
    return String(asOf.diff(birth, 'year'));
};

/** True when the instant falls on midnight UTC (date-only or “all-day” FHIR-style encodings). */
function isUtcMidnightOnly(isoLike: string): boolean {
    const d = dayjs.utc(isoLike);
    if (!d.isValid()) return false;
    return d.hour() === 0 && d.minute() === 0 && d.second() === 0 && d.millisecond() === 0;
}

/**
 * EMR / FHIR mixed values: compact YYYYMMDD, ISO date-only, or datetimes.
 * Shows local date+time when a non-midnight-UTC time is present; otherwise civil date-only
 * (avoids TZ day-shift for DOB and other date semantics).
 */
export const formatEmrDateInput = (input?: string | null): string => {
    if (input == null || String(input).trim() === '') {
        return '-';
    }
    const s = String(input).trim();
    if (/^\d{8}$/.test(s)) {
        return formatCivilDate(s);
    }
    const hasTime = /T|\d{2}:\d{2}/.test(s);
    if (hasTime) {
        if (isUtcMidnightOnly(s)) {
            return formatCivilDate(s);
        }
        return formatInstant(s);
    }
    return formatCivilDate(s);
};
